<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Notes
          </div>
        </template>
        <template #actions
          v-if="currentApplication === 'personId'
          && !isRetired
          && (hasModule('PRSID:PRPRF:NOTES:ADD:ADMIN')
            || hasModule('PRSID:PRPRF:NOTES:ADD:GENERAL'))
          && personTypeAccess"
        >
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <router-link
                  :to="{
                    name: 'PidAddNote',
                    query: {
                      retired: isRetired,
                    },
                  }"
                >
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                  size="lg"
                  title="Add"
                />
                </router-link>
              </div>
            </li>
          </ul>
        </template>
      </es-header>
    </div>
    <div class="content">
      <es-loader
        v-if="loading"
        style="margin: 6.5rem"
      />
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception
        v-else-if="!masterNotes.length"
        exceptionMessage="No Notes for this Person."
        margin="3rem"
      />
      <div v-else>
        <table class="table">
          <es-sorter
            :columnHeaders= "myColumnHeaders"
            @updateSorter="updateSorter"
          />
          <tbody v-if="!masterNotes.length">
            <td
              class="td-alert"
              colspan="100"
            >
              <es-exception
                exceptionMessage="No Notes for this Person."
                margin="3rem"
              />
            </td>
          </tbody>
          <template v-else>
            <person-profile-notes-row
              v-for="(note, id) in masterNotes"
              :key="id"
              :note="note"
            />
          </template>
        </table>
        <es-pager
          :totalCount="totalCount"
          :pageSize="5"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import FieldLabels from '@/mixins/FieldLabels';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import PersonProfileNotesRow from '@/components/PersonProfileNotesRow.vue';
import PermissionManager from '@/mixins/PermissionManager';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

library.add(
  faPlus,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
    PersonProfileNotesRow,
  },
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      masterNotes: [],
      totalCount: null,
    };
  },
  async created() {
    this.initializeColumnHeaders();
    this.getNotes();
  },
  computed: {
    currentApplication() {
      return this.$store.state.appModules.activeModule.name;
    },
    personTypeAccess() {
      if (this.$store.state.user.userPersonTypes
        .some(item => item.personType.code === this.person.personType)) {
        return true;
      }
      return false;
    },
  },
  watch: {
    $route() {
      this.getNotes();
      if (this.certified) {
        this.certified = false;
      }
    },
  },
  methods: {
    initializeColumnHeaders() {
      this.myColumnHeaders = [
        {
          header: 'Note Date',
          title: 'Note Date',
          sortable: true,
          field: 'lastUpdateDate',
          order: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Created By',
          title: 'Created By',
          sortable: true,
          field: 'createdBy',
          order: null,
          style: {
            idth: '15%',
          },
        },
        {
          header: 'Type',
          title: 'Type',
          sortable: true,
          field: 'type',
          order: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Note',
          title: 'Note',
          sortable: true,
          field: 'content',
          order: null,
          style: {
            width: '65%',
          },
        },
      ];
    },
    updateSorter(header) {
      const params = {};
      if (this.isRetired) {
        params.retired = true;
      }
      params.sortField = header.field;
      params.sortOrder = header.order;
      this.$router.push({ query: { ...params } });
    },
    async getNotes() {
      this.loading = true;
      this.loadingError = false;
      try {
        let res = null;
        const params = new URLSearchParams();
        params.append('pageSize', 5);
        params.append('page', this.$route.query.page ? this.$route.query.page : 1);
        const sort = [{
          field: this.$route.query.sortField ? this.$route.query.sortField : 'lastUpdateDate',
          order: this.$route.query.sortOrder ? this.$route.query.sortOrder : 'DESC',
        }];
        params.append('sort', JSON.stringify(sort));
        if (this.hasModule('PRSID:PRPRF:NOTES:VIEW:ADMIN')) {
          params.append('type', 'ADMIN');
        }
        if (this.hasModule('PRSID:PRPRF:NOTES:VIEW:GENERAL')) {
          params.append('type', 'GENERAL');
        }
        if (this.isRetired) {
          res = await axios.get(`retired-person/${this.person.personId}/notes`, { params });
        } else {
          res = await axios.get(`person/${this.person.stateStudentId}/notes`, { params });
        }
        this.totalCount = res.data.totalCount;
        this.masterNotes = res.data.data;
      } catch (error) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
